import React, { useState, useEffect, useCallback } from 'react';
import Skeleton from '../common/Skeleton';
import { Check, CircleMinus, CircleCheck, Edit2, Lock } from 'lucide-react';
import riskKRIsService, { RiskKRI } from '../../services/riskKrisService';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmWarning, confirmActive } from '../common/confirm';
import extractApiError from '../../utils/apiError';
import NoDataFound from '../common/NoDataFound';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea } from '../../validation/ValidatedFields';
import { isUniqueLocal } from '../../validation/uniqueness';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';
import { RBAC_ACTIVITY } from './rbacMock';
import ApprovalDot, { WorkflowState } from '../common/ApprovalDot';
import WorkflowModal from '../common/WorkflowModal';
import WorkflowSaveBar, { SaveTarget } from '../common/WorkflowSaveBar';

const WF_KIND = 'rsk_kri';

interface RiskKrisTabProps {
  createTrigger?: number;
  pageSize: number;
  onPageSizeChange?: (size: number) => void;
  searchTerm: string;
  statusFilter: string;
}

const RiskKrisTab: React.FC<RiskKrisTabProps> = ({ createTrigger, pageSize, onPageSizeChange, searchTerm, statusFilter }) => {
  const viewPerm = usePermission(RBAC_ACTIVITY, 'view');
  const canView = viewPerm.allowed;
  const { allowed: canModify, loading: permLoading } = usePermission(RBAC_ACTIVITY, 'view+modify');
  const { allowed: canApproveExtent } = usePermission(RBAC_ACTIVITY, 'view+modify+approve');
  const [kris, setKris] = useState<RiskKRI[]>([]);
  const [workflowRow, setWorkflowRow] = useState<RiskKRI | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedKRI, setSelectedKRI] = useState<RiskKRI | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // View-first edit: existing records open read-only; the "Edit" button enables the form.
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<RiskKRI>>({});
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const fieldVh = useFieldValidation();

  const loadKRIs = useCallback(async () => {
    try {
      setLoading(true);
      const data = await riskKRIsService.getAll({
        status: statusFilter as any,
        search: searchTerm || undefined,
        limit: 100
      });
      const sorted = [...data].sort((a, b) => (b.id || 0) - (a.id || 0));
      setKris(sorted);
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to load KRIs' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm]);

  useEffect(() => { if (canView) loadKRIs(); }, [loadKRIs, canView]);
  useEffect(() => { setPage(1); }, [searchTerm, statusFilter, pageSize]);
  useEffect(() => { if (createTrigger && canModify) handleAddNew(); }, [createTrigger]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setSubmitting(false);
    setToggling(false);
  }, [showModal]);

  const handleRowClick = (kri: RiskKRI) => {
    setSelectedKRI(kri);
    setFormData(kri);
    fieldVh.reset();
    setIsEditMode(true);
    setIsEditing(false); // open read-only; "Edit" enables the form
    setShowModal(true);
  };

  const handleAddNew = () => {
    if (!canModify) return;
    setSelectedKRI(null);
    setFormData({ kri_name: '', description: '', is_active: 1 });
    fieldVh.reset();
    setIsEditMode(false);
    setIsEditing(true); // create is always editable
    setShowModal(true);
  };

  // Approved state — determines if form is locked
  const isApproved = selectedKRI?.workflow_state === 'approved';
  // Read-only while viewing an existing record (before "Edit"), or when lacking modify rights.
  const fieldsDisabled = isEditMode && (!isEditing || !canModify || isApproved);

  const persist = async (action: SaveTarget) => {
    if (fieldsDisabled) return;
    const valid = fieldVh.validateAll([
      { name: 'kri_name', code: 109, value: (formData.kri_name || '').trim(), required: true },
      { name: 'description', code: 110, value: formData.description || '' },
    ]);
    if (!valid) return;
    if (!isUniqueLocal(949, (formData.kri_name || '').trim(), kris, selectedKRI?.id)) {
      fieldVh.setError('kri_name', 'KRI with this name already exists');
      return;
    }
    setSubmitting(true);
    try {
      if (isEditMode && selectedKRI) {
        const { is_active: _ia, ...updateData } = formData;
        await riskKRIsService.update(selectedKRI.id, updateData);
        setToast({ type: 'success', message: 'KRI updated successfully.' });
      } else {
        await riskKRIsService.create(formData, action);
        setToast({
          type: 'success', message: ({
            draft: 'KRI saved as draft.',
            submit: 'KRI submitted for approval.',
            approve: 'KRI created & approved.',
          } as Record<SaveTarget, string>)[action]
        });
      }
      setShowModal(false);
      loadKRIs();
    } catch (err: any) {
      const msg = extractApiError(err, 'Operation failed');
      if (/exist|duplicate|unique/i.test(msg)) {
        fieldVh.setError('kri_name', msg);
      } else {
        setToast({ type: 'error', message: msg });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Edit form submit (Enter / Update button) → plain update.
  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); persist('draft'); };
  // Create express-save targets (Save as Draft / Submit / Approve).
  const handleCreateSave = (target: SaveTarget) => persist(target);

  const handleToggleActive = async () => {
    if (!selectedKRI || !canModify) return;
    const next = selectedKRI.is_active ? 0 : 1;
    const confirmFn = selectedKRI.is_active ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: next ? 'Reactivate this KRI?' : 'Inactivate this KRI?',
      text: next ? 'It will appear in active lists again.' : 'It will be hidden from active lists.',
      confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
    });
    if (!ok) return;
    setToggling(true);
    try {
      await riskKRIsService.toggleStatus(selectedKRI.id);
      setShowModal(false);
      setToast({ type: next ? 'success' : 'warning', message: next ? 'KRI Reactivated.' : 'KRI Inactivated.' });
      loadKRIs();
    } catch (err: any) {
      setToast({ type: 'error', message: extractApiError(err, 'Failed to update status') });
    } finally {
      setToggling(false);
    }
  };

  const totalRecords = kris.length;
  const paginatedRows = kris.slice((page - 1) * pageSize, page * pageSize);

  // ── Not-authorised gate ──────────────────────────────────────────────────
  if (!viewPerm.loading && !canView) {
    return (
      <NoDataFound
        title="Not authorised"
        message={viewPerm.message || NOT_AUTHORISED_MSG}
      />
    );
  }

  return (
    <>
      <div className="scroll-container flex-1 overflow-hidden">
        <div className="table-list">
          <div className="table-list-scroll">
            <table className="table-list-table">
              <thead>
                <tr>
                  <th className="table-list-th table-list-cell--dot" />
                  <th className="table-list-th table-list-cell--sno">S.No</th>
                  <th className="table-list-th table-list-cell--code">Code</th>
                  <th className="table-list-th">KRI Name</th>
                  <th className="table-list-th">Description</th>
                </tr>
              </thead>
              <tbody aria-busy={loading} aria-live="polite">
                {loading && <Skeleton.TableRows cols={['3xs', '3xs', 'md', 'md']} />}
                {!loading && kris.length === 0 && (
                  <tr>
                    <td colSpan={5} className="table-list-cell">
                      <NoDataFound title="No KRIs Found" message='Click "Create" to add one.' />
                    </td>
                  </tr>
                )}
                {!loading && paginatedRows.map((kri, idx) => (
                  <tr data-testid={`BTN_risk_kri_row_${kri.id}_open`} key={kri.id} className={`table-list-row${kri.is_active === 1 ? '' : ' is-inactive'}`} onClick={() => handleRowClick(kri)}>
                    <td data-testid={`BTN_risk_kri_row_${kri.id}_workflow`} className="table-list-cell table-list-cell--dot" onClick={(e) => { e.stopPropagation(); setWorkflowRow(kri); }} title="Workflow — view timeline & act">
                      <ApprovalDot
                        state={(kri.workflow_state || 'draft') as WorkflowState}
                        onClick={() => setWorkflowRow(kri)}
                      />
                    </td>
                    <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                    <td className="table-list-cell table-list-cell--code">
                      {kri.code ? <span className="cell-code">{kri.code}</span> : <span className="text-gray-400 italic">—</span>}
                    </td>
                    <td className="table-list-cell">
                      <span className="table-list-cell-title cell-name-lock">
                        {kri.workflow_state === 'approved' && (
                          <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                        )}
                        {kri.kri_name}
                      </span>
                    </td>
                    <td className="table-list-cell table-list-cell--truncate" title={kri.description || ''}>
                      {kri.description || <span className="text-gray-400 italic">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={setPage}
            onPageSizeChange={onPageSizeChange}
            recordLabel="KRIs"
          />
        </div>
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} size="md">
        <Modal.Header>
          {isEditMode && selectedKRI
            ? <>{isApproved ? 'Viewing (locked)' : (isEditing ? 'Editing' : 'Viewing')} "<span className="item-highlight">{selectedKRI.kri_name}</span>"</>
            : 'Create KRI'}
        </Modal.Header>
        <Modal.Body>
          {isApproved && (
            <div className="approved-banner">
              <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
            </div>
          )}
          <form data-testid="MOD_risk_kri_form" id="kri-form" onSubmit={handleSubmit} className="flex flex-col gap-y-4">
            {/* System code — read-only, prefilled with the same code the list shows. */}
            {isEditMode && (
              <FormControl.Input
                label="Code"
                value={(selectedKRI as any)?.code || '—'}
                disabled
                data-testid="INP_risk_kri_code"
              />
            )}
            <ValidatedInput
              vh={fieldVh}
              name="kri_name"
              code={109}
              required
              label="KRI Name"
              type="text"
              data-testid="INP_risk_kri_name"
              value={formData.kri_name || ''}
              onChange={e => setFormData({ ...formData, kri_name: e.target.value })}
              placeholder="e.g., Customer Complaints Rate, System Downtime"
              disabled={fieldsDisabled}
            />
<ValidatedTextarea
              vh={fieldVh}
              name="description"
              code={110}
              label="Description"
              data-testid="INP_risk_kri_description"
              value={formData.description || ''}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="Describe this KRI…"
              disabled={fieldsDisabled}
            />
          </form>
        </Modal.Body>
        <Modal.Footer className={isEditMode ? 'app-modal-footer--split' : ''}>
          {isEditMode && selectedKRI && !isApproved && (
            <Button
              data-testid="BTN_risk_kri_toggle_active"
              variant={selectedKRI.is_active ? 'inactive-outline' : 'active-outline'}
              icon={selectedKRI.is_active ? CircleMinus : CircleCheck}
              onClick={handleToggleActive}
              disabled={permLoading || !canModify || toggling || submitting}
              title={!canModify ? NOT_AUTHORISED_MSG : undefined}
            >
              {toggling ? '...' : selectedKRI.is_active ? 'Inactive' : 'Active'}
            </Button>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            {isEditMode ? (
              !isEditing && !isApproved ? (
                <Button
                  data-testid="BTN_risk_kri_edit"
                  variant="fill"
                  icon={Edit2}
                  onClick={() => {
                    if (permLoading || !canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
                    setIsEditing(true);
                  }}
                  disabled={permLoading || !canModify}
                  title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                >
                  Edit
                </Button>
              ) : isEditing && !isApproved ? (
                <Button
                  data-testid="BTN_risk_kri_update"
                  variant="update"
                  icon={Check}
                  onClick={handleSubmit}
                  disabled={permLoading || !canModify || submitting || toggling}
                  title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                >
                  {submitting ? 'Updating...' : 'Update'}
                </Button>
              ) : null
            ) : (
              <WorkflowSaveBar
                canDraft={canModify}
                canSubmit={canModify}
                canApprove={canApproveExtent}
                busy={submitting}
                onSave={handleCreateSave}
              />
            )}
          </div>
        </Modal.Footer>
      </Modal>

      <WorkflowModal
        isOpen={!!workflowRow}
        onClose={() => { setWorkflowRow(null); loadKRIs(); }}
        kind={WF_KIND}
        id={workflowRow?.id ?? null}
        onChanged={loadKRIs}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

export default RiskKrisTab;
